import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Eye, CheckCircle, Receipt, Printer, Pencil, FileDown, Download } from "lucide-react";
import { toast } from "sonner";
import { getCustomers, getBills, editBill } from "@/lib/api";
import { exportCSV, exportPDF } from "@/lib/exportUtils";
import PaymentDialog from "@/components/PaymentDialog";
import FilterBar, { useSessionFilters } from "@/components/FilterBar";

const BILL_FILTER_DEFAULTS = {
  society: "all", tower: "all", paymentStatus: "all",
  billDateFrom: "", billDateTo: "", dueDateFrom: "", dueDateTo: "",
  amountMin: "", amountMax: "",
};

export default function BillHistory() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);
  const [payBill, setPayBill] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editOpen, setEditOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [editForm, setEditForm] = useState({ billDate: "", currentReading: 0, netPayable: 0 });
  const [filters, setFilters] = useSessionFilters("bills", BILL_FILTER_DEFAULTS);

  useEffect(() => {
    getCustomers().then(setCustomers).catch((e) => { console.warn("Failed to load customers:", e.message); });
    getBills().then(setBills).catch((e) => { console.warn("Failed to load bills:", e.message); });
    const saved = sessionStorage.getItem("bulkPrintSelectedIds");
    if (saved) {
      try { setSelectedIds(new Set(JSON.parse(saved))); } catch (e) { console.warn("Failed to restore selections:", e.message); }
      sessionStorage.removeItem("bulkPrintSelectedIds");
    }
  }, []);

  const societies = useMemo(() => Array.from(new Set(bills.map((b) => b.society).filter(Boolean))).sort(), [bills]);
  const towers = useMemo(() => {
    const src = filters.society !== "all" ? bills.filter((b) => b.society === filters.society) : bills;
    return Array.from(new Set(src.map((b) => b.towerNo).filter(Boolean))).sort();
  }, [bills, filters.society]);

  const filterConfig = useMemo(() => [
    { key: "society", label: "Society", type: "select", options: [{ value: "all", label: "All Societies" }, ...societies.map((s) => ({ value: s, label: s }))] },
    { key: "tower", label: "Tower", type: "select", options: [{ value: "all", label: "All Towers" }, ...towers.map((t) => ({ value: t, label: t }))] },
    { key: "paymentStatus", label: "Status", type: "select", options: [{ value: "all", label: "All" }, { value: "paid", label: "Paid" }, { value: "unpaid", label: "Unpaid" }] },
    { key: "billDateFrom", label: "Bill From", type: "date" },
    { key: "billDateTo", label: "Bill To", type: "date" },
    { key: "dueDateFrom", label: "Due From", type: "date" },
    { key: "dueDateTo", label: "Due To", type: "date" },
    { key: "amountMin", label: "Min Amount", type: "amount", placeholder: "₹ Min" },
    { key: "amountMax", label: "Max Amount", type: "amount", placeholder: "₹ Max" },
  ], [societies, towers]);

  const filtered = useMemo(() => {
    return bills.filter((b) => {
      if (filters.society !== "all" && b.society !== filters.society) return false;
      if (filters.tower !== "all" && b.towerNo !== filters.tower) return false;
      if (filters.paymentStatus === "paid" && !b.isPaid) return false;
      if (filters.paymentStatus === "unpaid" && b.isPaid) return false;
      if (filters.billDateFrom && b.billDate < filters.billDateFrom) return false;
      if (filters.billDateTo && b.billDate > filters.billDateTo) return false;
      if (filters.dueDateFrom && b.dueDate < filters.dueDateFrom) return false;
      if (filters.dueDateTo && b.dueDate > filters.dueDateTo) return false;
      if (filters.amountMin && (b.netPayable || 0) < parseFloat(filters.amountMin)) return false;
      if (filters.amountMax && (b.netPayable || 0) > parseFloat(filters.amountMax)) return false;
      return true;
    }).sort((a, b) => new Date(b.billDate) - new Date(a.billDate));
  }, [bills, filters]);

  const toggleId = (id) => setSelectedIds((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = (c) => { if (c) setSelectedIds(new Set(filtered.map((b) => b.id))); else setSelectedIds(new Set()); };
  const allSelected = filtered.length > 0 && filtered.every((b) => selectedIds.has(b.id));

  const handleBulkPrint = () => {
    sessionStorage.setItem("bulkPrintSelectedIds", JSON.stringify(Array.from(selectedIds)));
    navigate(`/bills/bulk-print?ids=${Array.from(selectedIds).join(",")}`);
  };

  const openEditBill = (bill) => {
    setEditingBill(bill);
    setEditForm({ billDate: bill.billDate, currentReading: bill.currentReading, netPayable: bill.netPayable });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingBill) return;
    await editBill(editingBill.id, editForm);
    setBills(await getBills());
    setEditOpen(false);
    toast.success("Bill updated");
  };

  const handleExportCSV = () => {
    const data = selectedIds.size > 0 ? filtered.filter((b) => selectedIds.has(b.id)) : filtered;
    exportCSV(data, [
      { label: "Invoice No", accessor: (r) => r.invoiceNo },
      { label: "Customer", accessor: (r) => r.customerName },
      { label: "Bill Date", accessor: (r) => new Date(r.billDate).toLocaleDateString("en-IN") },
      { label: "Units", accessor: (r) => (r.consumedUnits || 0).toFixed(2) },
      { label: "Net Payable", accessor: (r) => (r.netPayable || 0).toFixed(2) },
      { label: "Status", accessor: (r) => r.isPaid ? "Paid" : "Unpaid" },
    ], "bills");
  };

  const handleExportPDF = () => {
    const data = selectedIds.size > 0 ? filtered.filter((b) => selectedIds.has(b.id)) : filtered;
    exportPDF("Bill History", [
      { label: "Invoice No", accessor: (r) => r.invoiceNo },
      { label: "Customer", accessor: (r) => r.customerName },
      { label: "Bill Date", accessor: (r) => new Date(r.billDate).toLocaleDateString("en-IN") },
      { label: "Units", accessor: (r) => (r.consumedUnits || 0).toFixed(2) },
      { label: "Amount", accessor: (r) => (r.netPayable || 0).toFixed(2) },
      { label: "Status", accessor: (r) => r.isPaid ? "Paid" : "Unpaid" },
    ], data, "bill_history");
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="bill-history-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">Bill History</h1>
        <div className="flex flex-wrap gap-2">
          {selectedIds.size > 0 && (
            <Button data-testid="bulk-print-selected" onClick={handleBulkPrint} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm text-xs">
              <Printer className="w-3 h-3 mr-1" /> Print {selectedIds.size}
            </Button>
          )}
          <Button data-testid="export-bills-pdf" variant="outline" size="sm" onClick={handleExportPDF} className="rounded-sm text-xs">
            <Download className="w-3 h-3 mr-1" /> PDF
          </Button>
          <Button data-testid="export-bills-csv" variant="outline" size="sm" onClick={handleExportCSV} className="rounded-sm text-xs">
            <FileDown className="w-3 h-3 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <FilterBar
        pageKey="bills"
        defaults={BILL_FILTER_DEFAULTS}
        config={filterConfig}
        filters={filters}
        setFilters={setFilters}
        filteredCount={filtered.length}
        totalCount={bills.length}
      />

      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-10"><Checkbox data-testid="select-all-bills" checked={allSelected} onCheckedChange={toggleAll} /></TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Invoice No</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Customer</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Bill Date</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right">Units</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right">Net Payable</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Status</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right sticky right-0 bg-zinc-50">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-400 py-12">No bills found.</TableCell></TableRow>
                ) : filtered.map((bill) => (
                  <TableRow key={bill.id} data-testid={`bill-row-${bill.id}`} className={`hover:bg-zinc-50 ${selectedIds.has(bill.id) ? "bg-orange-50/50" : ""}`}>
                    <TableCell><Checkbox data-testid={`select-bill-${bill.id}`} checked={selectedIds.has(bill.id)} onCheckedChange={() => toggleId(bill.id)} /></TableCell>
                    <TableCell className="font-mono text-sm">{bill.invoiceNo}</TableCell>
                    <TableCell className="text-sm">{bill.customerName}</TableCell>
                    <TableCell className="text-sm">{new Date(bill.billDate).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell className="font-mono text-sm text-right">{(bill.consumedUnits || 0).toFixed(2)}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-right">₹{(bill.netPayable || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {(() => {
                        const s = bill.status || (bill.isPaid ? "paid" : "unpaid");
                        if (s === "paid") return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Paid</Badge>;
                        if (s === "partial") return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Partial</Badge>;
                        return <Badge variant="destructive" className="text-[10px]">Unpaid</Badge>;
                      })()}
                    </TableCell>
                    <TableCell className="text-right sticky right-0 bg-white">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/bills/invoice/${bill.id}`)} title="View"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditBill(bill)} title="Edit"><Pencil className="w-4 h-4" /></Button>
                        {(!bill.isPaid || bill.status === "partial") && (
                          <Button size="sm" onClick={() => setPayBill(bill)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-[10px] px-2">Pay</Button>
                        )}
                        {(bill.isPaid || bill.status === "partial") && (bill.paymentDetails?.receiptNo || bill.payments?.length > 0) && (
                          <Button variant="outline" size="sm" onClick={() => navigate(`/bills/receipt/${bill.id}`)} className="text-[10px] rounded-sm px-2">Rcpt</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PaymentDialog bill={payBill} open={!!payBill} onOpenChange={(o) => { if (!o) setPayBill(null); }} onPaid={() => { getBills().then(setBills); setPayBill(null); }} />

      {/* Edit Bill Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Edit Bill</DialogTitle>
            <DialogDescription>{editingBill?.invoiceNo} — {editingBill?.customerName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Bill Date</Label>
              <Input data-testid="edit-bill-date" type="date" value={editForm.billDate} onChange={(e) => setEditForm((f) => ({ ...f, billDate: e.target.value }))} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs">Current Reading</Label>
              <Input data-testid="edit-current-reading" type="number" step="0.01" value={editForm.currentReading} onChange={(e) => setEditForm((f) => ({ ...f, currentReading: parseFloat(e.target.value) || 0 }))} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs">Net Payable (₹)</Label>
              <Input data-testid="edit-net-payable" type="number" step="0.01" value={editForm.netPayable} onChange={(e) => setEditForm((f) => ({ ...f, netPayable: parseFloat(e.target.value) || 0 }))} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-edit-bill" onClick={handleEditSave} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
